// src/stores/usersStore.js
import { defineStore } from 'pinia'
import { db } from '@/services/firebaseconfig'
import { collection, query } from 'firebase/firestore'
import { onSnapshot } from '@/services/sessionListeners'
import { useUserStore } from './userStore'
import { usePermissionsStore } from '@/stores/permissionsStore'
import { isSelectableUser } from '@/utils/selectorEligibility'

export const useUsersStore = defineStore('usersStore', {
  state: () => ({
    users: {}, // { userId: { displayName, email, ... } }
    isLoading: false,
    error: null,
    hasFetched: false,
    lastFetched: 0, // ms epoch
    _inFlight: null, // Promise | null
    _unsubscribe: null,
  }),

  getters: {
    selectableUsers: state =>
      Object.fromEntries(Object.entries(state.users).filter(([, user]) => isSelectableUser(user))),
  },

  actions: {
    /**
     * Idempotent users fetch that is now directory-aware.
     * - Admins/Ops: fetch full /users (includes sensitive fields; allowed for them).
     * - Everyone else: fetch /publicUsers (safe directory fields only).
     *
     * This prevents "Unknown" name pills for non-admins without exposing commission rates.
     */
    async fetchAllUsers(opts = {}) {
      const { force = false } = opts
      const userStore = useUserStore()
      const currentUser = userStore.user

      if (!currentUser) {
        this._unsubscribe?.()
        this._unsubscribe = null
        this.users = {}
        this.hasFetched = true
        return
      }

      // Reuse in-flight request
      if (this._inFlight) return this._inFlight
      if (!force && this._unsubscribe?.active) return

      this.isLoading = true
      this.error = null

      this._inFlight = (async () => {
        let perfLabel = '[PERF] [usersStore] fetchAllUsers'
        try {
          // Normalize roles from V2 nested `roles.userRole`
          const rawUserRole = currentUser.roles?.userRole
          const rawRoles = Array.isArray(rawUserRole)
            ? rawUserRole
            : rawUserRole
              ? [rawUserRole]
              : []

          const roles = rawRoles
            .map(r => (r && typeof r === 'object' ? r.id || r.value || r.name || r.label || '' : r))
            .filter(Boolean)
            .map(r => String(r).trim().toLowerCase())

          // Wait for permissions to load before checking collection access
          const permStore = usePermissionsStore()
          await permStore.waitForInit()

          const isSA = currentUser.roles?.isSuperAdmin === true
          const isPrivileged = permStore.hasFullVisibility('users', roles, isSA)

          perfLabel = `[PERF] [usersStore] fetchAllUsers (privileged=${isPrivileged}) roles=${roles.join(',') || 'none'}`
          console.time(perfLabel)

          let usersQuery

          if (isPrivileged) {
            // Admin/Ops can read full users (includes sensitive fields like commissionRate).
            usersQuery = query(collection(db, 'users'))
          } else {
            // Non-admin roles read safe directory from publicUsers.
            usersQuery = query(collection(db, 'publicUsers'))
          }

          this._unsubscribe?.()
          await new Promise((resolve, reject) => {
            this._unsubscribe = onSnapshot(
              usersQuery,
              snapshot => {
                const usersMap = Object.fromEntries(snapshot.docs.map(doc => [doc.id, doc.data()]))
                // A live status always wins over the session's cached user record.
                if (!usersMap[currentUser.uid]) usersMap[currentUser.uid] = currentUser
                this.users = usersMap
                this.hasFetched = true
                this.lastFetched = Date.now()
                resolve()
              },
              err => {
                this.error = err
                reject(err)
              }
            )
          })
        } catch (err) {
          console.error('❌ Error fetching users directory:', err)
          this.error = err

          // Safety fallback: at least include current user so UI can show their own name
          this.users = { [currentUser.uid]: currentUser }
          // Leave hasFetched = false so subsequent calls retry instead of silently returning stale data
        } finally {
          this.isLoading = false
          console.timeEnd(perfLabel)
          this._inFlight = null
        }
      })()

      return this._inFlight
    },
  },
})
